import React, { useState } from "react";
import { MdLocationOn, MdArrowForward } from "react-icons/md";
import { IoMdWalk, IoMdColorPalette, IoMdCar } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";

const CLASS_LABELS = { 1: "A", 2: "B1", 3: "B2", 4: "C1", 5: "C2" };
const imagesUrl = `${process.env.REACT_APP_API_URL}/api/images`;
const fallbackImage = `${imagesUrl}/90ef/cover-image-1646409988773.jpeg`;

// Shared card for hostel/homestel listings and related residences.
// `to` is where the card links; `hardNavigate` forces a full page load
// (used on the details page, where only the slug changes).
export default function ResidenceCard({ residence, to, hardNavigate = false }) {
	const navigate = useNavigate();
	// null = overlay follows hover, true/false = toggled by the class badge
	const [active, setActive] = useState(null);

	const classLabel = CLASS_LABELS[residence?.rClass?.id];
	const className = residence?.rClass?.name;
	const isDrive = residence?.distanceType === `1`;
	const coverImage = residence?.coverImage
		? `${imagesUrl}/${residence.slug}/${residence.coverImage}`
		: fallbackImage;

	const open = () => {
		if (hardNavigate) window.location.assign(to);
		else navigate(to);
	};

	const handleTitleClick = (e) => {
		if (!hardNavigate) return;
		e.preventDefault();
		open();
	};

	const toggleOverlay = (e) => {
		e.stopPropagation();
		setActive(!active);
	};

	const overlayState =
		active == null ? "" : active ? " is-open" : " is-closed";

	return (
		<article className="rc-card">
			<div
				className="rc-media"
				role="link"
				tabIndex={0}
				aria-label={`View ${residence?.name}`}
				onClick={open}
				onKeyDown={(e) => e.key === "Enter" && open()}
			>
				<img
					src={coverImage}
					alt={residence?.name || "Residence"}
					loading="lazy"
					onError={(e) => {
						if (e.currentTarget.src !== fallbackImage)
							e.currentTarget.src = fallbackImage;
					}}
				/>

				<button
					type="button"
					className={`rc-badge${classLabel ? "" : " rc-badge--none"}`}
					title="What does this class mean?"
					aria-pressed={!!active}
					onClick={toggleOverlay}
				>
					{classLabel || "N/A"}
				</button>

				<div className={`rc-overlay${overlayState}`}>
					{className ? (
						<p>
							The residence has been classified to <b>{className}</b> based on
							the <b>Location</b>, <b>Available Facilities</b>,{" "}
							<b>Utilities</b> &amp; <b>Services</b> provided
						</p>
					) : (
						<p>The residence has not been classified yet</p>
					)}
				</div>
			</div>

			<div className="rc-body">
				<h5 className="rc-title" title={residence?.name}>
					<Link to={to} onClick={handleTitleClick}>
						{residence?.name}
					</Link>
				</h5>

				<ul className="rc-meta">
					<li>
						<span className="rc-icon">
							<MdLocationOn />
						</span>
						<span>
							<span className="rc-label">Location</span>
							<span className="rc-value">
								{residence?.location?.name || "N/A"}
							</span>
						</span>
					</li>
					<li>
						<span className="rc-icon">{isDrive ? <IoMdCar /> : <IoMdWalk />}</span>
						<span>
							<span className="rc-label">Distance</span>
							<span className="rc-value">
								{residence?.distance
									? `${residence.distance} ${
											residence.distanceType === `0` ? "walk" : "drive"
									  } to Campus`
									: "N/A"}
							</span>
						</span>
					</li>
					<li>
						<span className="rc-icon">
							<IoMdColorPalette />
						</span>
						<span>
							<span className="rc-label">Category</span>
							<span className="rc-value">{className || "N/A"}</span>
						</span>
					</li>
				</ul>

				<div className="rc-footer">
					<Link to={to} onClick={handleTitleClick} className="rc-link">
						View details <MdArrowForward />
					</Link>
				</div>
			</div>
		</article>
	);
}
